/**
 * 学习进度（localStorage，纯前端）：{ "sectionId/lessonId": true }
 * React 侧用 useSyncExternalStore 订阅，跨组件实时联动（首页进度区 / 教学中心）。
 */
import { useSyncExternalStore } from 'react';
import { SECTIONS } from '../data/lessons.js';

const KEY = 'mofang.progress.v1';

export type ProgressMap = Record<string, boolean>;

interface LessonRef {
  key: string;
  sectionId: string;
  lessonId: string;
  title: string;
}

/** 全部课程（有序） */
export const ALL_LESSONS: LessonRef[] = (
  SECTIONS as { id: string; lessons: { id: string; title: string }[] }[]
).flatMap((sec) =>
  sec.lessons.map((l) => ({
    key: `${sec.id}/${l.id}`,
    sectionId: sec.id,
    lessonId: l.id,
    title: l.title,
  }))
);

/** 七步徽章对应的课程 key（beginner 的 step1~step7） */
export const STEP_BADGES: { key: string; label: string; icon: string }[] = [
  { key: 'beginner/step1-cross', label: '白十字', icon: '✚' },
  { key: 'beginner/step2-corners', label: '白角块', icon: '◧' },
  { key: 'beginner/step3-middle', label: '中层', icon: '▤' },
  { key: 'beginner/step4-top-cross', label: '黄十字', icon: '✛' },
  { key: 'beginner/step5-top-color', label: '全黄面', icon: '◉' },
  { key: 'beginner/step6-top-corners', label: '角归位', icon: '◩' },
  { key: 'beginner/step7-top-edges', label: '大功告成', icon: '★' },
];

/** CFOP 徽章（advanced 章节对应课程） */
export const CFOP_BADGES: { key: string; label: string; icon: string }[] = [
  { key: 'advanced/cross-advanced', label: '高效十字', icon: '⨯' },
  { key: 'advanced/f2l-cases', label: 'F2L', icon: '⧉' },
  { key: 'advanced/oll-2look', label: 'OLL', icon: '◍' },
  { key: 'advanced/pll-2look', label: 'PLL', icon: '⇄' },
];

/** 分轨完成度：只统计指定章节 */
export function trackPercent(map: ProgressMap, sectionIds: string[]): number {
  const pool = ALL_LESSONS.filter((l) => sectionIds.includes(l.sectionId));
  if (!pool.length) return 0;
  return Math.round((pool.filter((l) => map[l.key]).length / pool.length) * 100);
}

/** 某章节全部完成 */
export function sectionDone(map: ProgressMap, sectionIds: string[]): boolean {
  return ALL_LESSONS.filter((l) => sectionIds.includes(l.sectionId)).every((l) => map[l.key]);
}

function read(): ProgressMap {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as ProgressMap) : {};
  } catch {
    return {};
  }
}

let state: ProgressMap = read();
const listeners = new Set<() => void>();

function emit() {
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useProgress(): ProgressMap {
  return useSyncExternalStore(subscribe, () => state);
}

export function markLessonDone(key: string) {
  if (state[key]) return;
  state = { ...state, [key]: true };
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* 隐私模式等场景静默失败 */
  }
  emit();
}

export function isLessonDone(key: string): boolean {
  return !!state[key];
}

export function progressPercent(map: ProgressMap): number {
  const done = ALL_LESSONS.filter((l) => map[l.key]).length;
  return Math.round((done / ALL_LESSONS.length) * 100);
}

export function allDone(map: ProgressMap): boolean {
  return ALL_LESSONS.every((l) => map[l.key]);
}
